import React, { useState } from "react";
import {
    Box,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Icon,
    Paper,
    alpha,
    Chip,
} from "@mui/material";
import { DiscountSettingsForm } from "./DiscountSettingsForm";

// Mock navigation items for POS Configuration
const navigation = [
    {
        id: "general",
        name: "General Settings",
        icon: "settings",
        description: "Configure general POS behavior and defaults",
        current: true,
    },
    {
        id: "discount_settings",
        name: "Discount Settings",
        icon: "monetization_on",
        description: "Set discount limits for cashiers and supervisors",
        current: false,
    },
    {
        id: "printers",
        name: "Printers",
        icon: "printer",
        description: "Manage connected printers and receipts",
        current: false,
        comingSoon: true,
    },
    {
        id: "receipts",
        name: "Receipt Templates",
        icon: "description",
        description: "Customize receipt layouts and templates",
        current: false,
        comingSoon: true,
    },
    {
        id: "taxes",
        name: "Tax Rules",
        icon: "receipt_long",
        description: "Set tax rules and VAT configurations",
        current: false,
        comingSoon: true,
    },
];

export const POSConfiguration: React.FC = () => {
    const [activeTab, setActiveTab] = useState("general");

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
    };

    const renderContent = () => {
        switch (activeTab) {
            case "general":
                return (
                    <>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: alpha("#0891B2", 0.08),
                                    color: "#0891B2",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Icon>settings</Icon>
                            </Box>
                            <Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontFamily: "Montserrat",
                                        fontWeight: 600,
                                    }}
                                >
                                    General Settings
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontFamily: "Montserrat" }}
                                >
                                    Configure default behavior for your point-of-sale system
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    mb: 1,
                                    fontFamily: "Montserrat",
                                    fontWeight: 600,
                                }}
                            >
                                Default Language & Currency
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 3, fontFamily: "Montserrat" }}
                            >
                                These settings control the language and currency used by default in your POS interface.
                            </Typography>

                            {/* Placeholder for form */}
                            <Box
                                sx={{
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    p: 4,
                                    textAlign: "center",
                                    borderRadius: 1,
                                    bgcolor: alpha("#0891B2", 0.02),
                                }}
                            >
                                <Typography color="text.secondary">
                                    Language: English (default) | Currency: USD (default)
                                </Typography>
                                <Typography color="text.secondary" sx={{ mt: 1 }}>
                                    You can add a dropdown or modal here to change these values.
                                </Typography>
                            </Box>
                        </Box>
                    </>
                );
            case "discount_settings":
                return <DiscountSettingsForm />;
            default:
                return (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 8,
                            textAlign: "center",
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: 2,
                                bgcolor: alpha("#0891B2", 0.08),
                                color: "#0891B2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 3,
                            }}
                        >
                            <Icon sx={{ fontSize: 40 }}>rocket_launch</Icon>
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: "Montserrat",
                                fontWeight: 600,
                                color: "#0891B2",
                                mb: 1,
                            }}
                        >
                            Coming Soon!
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: "text.secondary",
                                fontFamily: "Montserrat",
                                maxWidth: 400,
                                mb: 3,
                            }}
                        >
                            We're working hard to bring you this feature. Stay tuned for updates!
                        </Typography>
                        <Chip
                            icon={<Icon>timer</Icon>}
                            label="Feature in Development"
                            sx={{
                                bgcolor: alpha("#0891B2", 0.08),
                                color: "#0891B2",
                                fontFamily: "Montserrat",
                                fontWeight: 500,
                            }}
                        />
                    </Box>
                );
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography
                variant="h5"
                sx={{
                    mb: 4,
                    fontFamily: "Montserrat",
                    fontWeight: 600,
                }}
            >
                POS Configuration
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    gap: 4,
                    maxWidth: 1200,
                    mx: "auto",
                }}
            >
                {/* Sidebar Navigation */}
                <Box
                    component="aside"
                    sx={{
                        width: 280,
                        flexShrink: 0,
                        display: { xs: "none", md: "block" },
                    }}
                >
                    <List
                        sx={{
                            p: 0,
                            borderRadius: 1,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        {navigation.map((item) => (
                            <ListItem
                                key={item.name}
                                onClick={() => handleTabChange(item.id)}
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    cursor: "pointer",
                                    bgcolor:
                                        item.id === activeTab
                                            ? alpha("#0891B2", 0.04)
                                            : "transparent",
                                    borderLeft: "3px solid",
                                    borderLeftColor:
                                        item.id === activeTab ? "#0891B2" : "transparent",
                                    transition: "all 0.2s ease-in-out",
                                    "&:hover": {
                                        bgcolor: alpha("#0891B2", 0.04),
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 40,
                                        color: item.id === activeTab ? "#0891B2" : "text.secondary",
                                    }}
                                >
                                    <Icon>{item.icon}</Icon>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontFamily: "Montserrat",
                                                    fontWeight: 600,
                                                    color:
                                                        item.id === activeTab ? "#0891B2" : "text.primary",
                                                }}
                                            >
                                                {item.name}
                                            </Typography>
                                            {item.comingSoon && (
                                                <Chip
                                                    size="small"
                                                    label="Soon"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: "0.625rem",
                                                        bgcolor: alpha("#0891B2", 0.1),
                                                        color: "#0891B2",
                                                        fontFamily: "Montserrat",
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "text.secondary",
                                                fontFamily: "Montserrat",
                                            }}
                                        >
                                            {item.description}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>

                {/* Main Content */}
                <Box sx={{ flex: 1 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                        }}
                    >
                        {renderContent()}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};